import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LANGUAGE,
  isRTL,
  languageFromPath,
  localePath,
  stripLanguage,
} from './index';

const LocaleContext = createContext({
  lang: DEFAULT_LANGUAGE,
  dir: 'ltr',
  path: (p) => p,
  canonicalPath: '/',
});

/**
 * Derives the active language from the URL (`/ar/...` → Arabic), keeps i18next
 * and the <html> element in sync with it, and hands children a `path()` helper
 * that keeps links inside the current language.
 */
export function LocaleProvider({ lang, children }) {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();

  const resolved = lang || languageFromPath(pathname);
  const dir = isRTL(resolved) ? 'rtl' : 'ltr';

  useEffect(() => {
    if (i18n.language !== resolved) i18n.changeLanguage(resolved);
  }, [i18n, resolved]);

  useEffect(() => {
    // Screen readers and the browser's own text handling rely on these, and
    // `dir` is what actually flips the layout for Arabic.
    document.documentElement.lang = resolved;
    document.documentElement.dir = dir;
    document.body.classList.toggle('rtl', dir === 'rtl');
  }, [resolved, dir]);

  const value = useMemo(() => ({
    lang: resolved,
    dir,
    path: (p) => localePath(p, resolved),
    canonicalPath: stripLanguage(pathname),
  }), [resolved, dir, pathname]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}
